<script setup lang="ts">
import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-light-svg-icons'

const props = withDefaults(defineProps<{
  id: string
  variant?: LayoutVariant
  shape?: SearchInputShape
  size?: InputSize
  name?: string
  label?: string
  // Set when the consumer provides its own visible label. Avoid adding a second label to the accessible name.
  externalLabel?: boolean
  resettable?: boolean
  placeholder?: string
  disabled?: boolean
  ariaDescribedby?: string
  color?: 'blurple' | 'carbon' | 'moss'
  modelValue?: any
}>(), {
  variant: 'blurple',
  shape: 'rounded',
  size: 'medium',
  color: 'blurple',
  resettable: false,
  disabled: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'reset'): void
}>()

const inputRef = useTemplateRef('input')

defineExpose({
  focus: () => {
    inputRef.value?.focus()
  },
})

const localValue = ref(props.modelValue || '')

watch(localValue, (newValue) => {
  emit('update:modelValue', newValue)
})

watch(() => props.modelValue, (newValue) => {
  localValue.value = newValue || ''
})

function reset() {
  if (props.resettable) {
    localValue.value = ''
    emit('reset')
  }
}
</script>

<template>
  <div :class="mergeClass(inputVariants.search.base({ size, shape, color }), disabled && 'bg-surface-primary dark:bg-surface-primary cursor-not-allowed')">
    <label v-if="!externalLabel" :for="id" class="sr-only">
      {{ label || 'Søk' }}
    </label>
    <input
      :id="id"
      ref="input"
      v-model="localValue"
      :name="name"
      :type="!resettable ? 'text' : 'search'"
      autocomplete="off"
      :placeholder="placeholder"
      :readonly="disabled"
      :aria-disabled="disabled ? 'true' : undefined"
      :aria-describedby="ariaDescribedby"
      :class="mergeClass(inputVariants.search.input(), disabled && 'cursor-not-allowed placeholder:text-secondary')"
    >
    <button
      class="transition-all ease-in-out duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
      :type="resettable && modelValue?.length > 0 ? 'button' : 'submit'"
      :disabled="disabled"
      :aria-label="resettable && localValue?.length ? 'Fjern søk' : 'Søk'"
      @click="reset"
    >
      <Icon :icon="resettable && localValue?.length ? faXmark : faMagnifyingGlass" size="sm" />
    </button>
  </div>
</template>
